import cx from "classnames";
import { Fragment, useEffect, useState } from "react";

import CentralizedModalContainer from "../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Icon,
  isNullish,
  Modal,
  Tabs
} from "@theorgchartdev/storybook";
import { ChangeChartView } from "../../Menus/ViewMenu/ViewMenu";
import { GetChartMetadata, UpdateChartMetadata } from "@/Utils/Promises";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { RemoveView } from "./ViewLibraryModal.util";
import { ViewLibraryTab } from "./ViewLibraryTab";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumUIModes,
  enumUISubModes,
  kAdminUser,
  kRWUser,
  DISPLAY_MODES,
  SEARCH_PARAM_DISPLAY_KEY
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumChartTemplateSet } from "@coreapp/constants";
import { isInViewManager } from "@coreapp/utils/isInViewManager";
import { useRouteState } from "@/hooks/useRouteState";

const ViewLibraryModal = props => {
  const viewManagerContext = useViewManagerContext();
  const vm = viewManagerContext.viewMgr.state;
  const { handleSideBarModeChange } = useRightSidebarContext();
  const { setParam: setRouteParam } = useRouteState();

  const [selectedId, setSelectedId] = useState(null);
  const [renamingId, _setRenamingId] = useState(null);
  const [isCreatingNew, _setCreatingNew] = useState(false);
  const [editingIds, setEditingIds] = useState(new Set());
  const [currentTab, _setCurrentTab] = useState(0);
  const [chartConfig, setChartConfig] = useState(null);
  const [initChartViews, setInitChartViews] = useState({});
  const [removedViewIds, setRemovedViews] = useState([]);

  const canEditView =
    !isNullish(selectedId) &&
    Globals._uiConfig.GetUIMode() !== enumUIModes.edit &&
    Globals._uiConfig.GetUISubMode() !== enumUISubModes.backgroundeditor &&
    !isInViewManager();
  const showEditViewTooltip = !isNullish(selectedId) && !canEditView;

  const setRenamingId = nextRenamingId => {
    if (!isNullish(nextRenamingId)) {
      setCreatingNew(false);
    }

    _setRenamingId(nextRenamingId);
  };

  const setCreatingNew = nextIsCreating => {
    const wsMgr = GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    if (nextIsCreating) {
      setRenamingId(null);
      eventTracker.ReportEvent("view_library.view.created");
    }
    _setCreatingNew(nextIsCreating);
  };

  const setCurrentTab = updater => {
    setCreatingNew(false);
    setSelectedId(null);
    _setCurrentTab(updater);
  };

  /*
   * When Modal is opened, we should check if there are any
   * shared views available. If there are, we should switch
   * to that tab.
   */
  useEffect(() => {
    if (!props.open) {
      return;
    }

    // Reload chart config
    LoadChartConfig();

    const wsMgr = GetWSMgr();

    setInitChartViews(() => {
      const baseCharts = wsMgr.fOrgInfo.GetMasterChartList();

      const chartViews = {};
      for (const chart of baseCharts) {
        chartViews[chart["ID"]] = structuredClone(chart["views"]);
      }

      return chartViews;
    });
    setRemovedViews([]);

    const ctm = wsMgr?.GetChartTemplatesMgr();
    if (!ctm) {
      return;
    }
    const sharedTemplates = ctm.GetTemplateList(enumChartTemplateSet.sharedtemplates);

    if (sharedTemplates.length === 0) {
      return;
    }

    setCurrentTab(1);
  }, [props.open]);

  //This shouldn't happen, but if somehow someone gets here we force break out
  if (![kAdminUser, kRWUser].includes(Globals._accountInfo.GetUserRole())) return;

  const PromptToSave = (shouldClose = false) => {
    let message = appstr("saveconfirm3");

    const affectedBaseCharts = getBaseChartsWithRemovedViews();
    if (affectedBaseCharts.length > 0) {
      message = `${appstr("confirmViewRemove1")} ${affectedBaseCharts.length} ${appstr(
        "confirmViewRemove2"
      )}`;
    }

    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      `${appstr("saveconfirm1")} ${appstr("View library")}${appstr("saveconfirm2")}`,
      message,
      handleSaveWithRemovedViews(affectedBaseCharts, shouldClose),
      handleReset(shouldClose, false),
      appstr("Save Changes"),
      appstr("Discard Changes")
    );
  };

  const handleCloseAttempt = () => {
    if (!vm.ChartTemplatesDirty()) {
      handleClose();
      return;
    }

    PromptToSave(true);
  };

  const handleClose = () => {
    setRenamingId(null);
    setSelectedId(null);
    setEditingIds(new Set());
    setCreatingNew(false);
    props.onClose();
  };

  const handleReset = shouldClose => () => {
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    const ctm = wsMgr.GetChartTemplatesMgr();
    // Setting the CTM as clean before reloading templates to
    // prevent the user from being Alerted twice about the changes
    ctm.ClearDirty();
    wsMgr.LoadChartTemplates();
    const baseCharts = wsMgr.fOrgInfo.GetMasterChartList();
    for (const chart of baseCharts) {
      const initViews = initChartViews[chart["ID"]];

      if (doc.GetChartID() === chart["ID"]) {
        const vm = doc.GetViewMgr();
        vm.ResetViewMgr();
        for (const viewId of initViews ?? []) {
          vm.AddView(viewId);
        }
      }

      if (!initViews) {
        continue;
      }

      chart["views"] = initViews;
    }

    if (shouldClose) {
      handleClose();
    }
  };

  const _handleSave = () => {
    const wsMgr = GetWSMgr();
    if (isCreatingNew) wsMgr.fPendingTemplateEvent = "view_library.view.created";
    wsMgr.SaveChartTemplates();
    vm.UpdateContext();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("view_library.view.saved");
  };

  const getBaseChartsWithRemovedViews = () => {
    const wsMgr = GetWSMgr();
    const allBaseCharts = wsMgr.fOrgInfo.GetMasterChartList();
    const affectedBaseCharts = [];
    for (const chart of allBaseCharts) {
      const views = chart["views"].filter(viewId => {
        return !removedViewIds.includes(viewId);
      });

      if (views.length === chart["views"].length) {
        continue;
      }

      affectedBaseCharts.push({
        ID: chart["ID"],
        views
      });
    }

    return affectedBaseCharts;
  };

  const handleSaveWithRemovedViews = (charts, shouldClose) => async () => {
    for (const chart of charts) {
      const data = JSON.stringify({
        views: chart["views"]
      });

      await UpdateChartMetadata(chart["ID"], data);
      updateViewsList(chart["ID"], chart["views"]);
    }

    _handleSave();

    if (shouldClose) {
      handleClose();
    }
  };

  const handleSave = () => {
    const affectedBaseCharts = getBaseChartsWithRemovedViews();

    if (affectedBaseCharts.length > 0) {
      PromptToSave();
      return;
    }
    _handleSave();
  };

  if (!props.open) {
    return null;
  }

  const handleAddTemplate = () => {
    // Switch from System to Shared Library
    if (currentTab === 0) {
      setCurrentTab(1);
    }

    setCreatingNew(true);
  };

  const handleTabChange = nextTabIndex => {
    const wsMgr = GetWSMgr();
    const ctm = wsMgr.GetChartTemplatesMgr();
    ctm.SwitchTemplates(nextTabIndex);
    setRenamingId(null);
    setCurrentTab(nextTabIndex);
  };

  const handleEdit = () => {
    // Set the "Active" chart
    ChangeChartView({ value: selectedId });

    // URL-driven sub-mode entry — TabularDisplayProvider observes ?display=
    // and syncs coreapp SubMode. Same helper ViewDisplay.editView uses —
    // all ?display= writes funnel through useRouteState.setParam.
    setRouteParam(SEARCH_PARAM_DISPLAY_KEY, DISPLAY_MODES.VIEWMANAGER);

    handleSideBarModeChange("view");
    const wsMgr = GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("view_library.view_editor.opened");

    handleCloseAttempt();
  };

  const LoadChartConfig = async () => {
    const doc = GetDoc();
    const chartID = doc.GetChartID();
    const { result } = await GetChartMetadata(chartID);
    setChartConfig(result);
  };

  const handleChartConfigUpdate = async data => {
    const doc = GetDoc();
    const chartID = doc.GetChartID();
    return await UpdateChartMetadata(chartID, JSON.stringify(data));
  };

  const getIsPromoted = viewId => {
    if (!chartConfig) {
      return null;
    }
    return chartConfig["views"].some(id => id === viewId);
  };

  const updateViewsList = (chartId, views) => {
    const wsMgr = GetWSMgr();
    const allBaseCharts = wsMgr.fOrgInfo.GetMasterChartList();
    for (const chart of allBaseCharts) {
      if (chart["ID"] != chartId) {
        continue;
      }

      chart["views"] = views;
    }
  };

  const handlePromote = async viewId => {
    const configPartial = {
      views: [...chartConfig["views"], viewId]
    };
    await handleChartConfigUpdate(configPartial);
    setChartConfig(p => ({ ...p, ...configPartial }));
    updateViewsList(chartConfig["ID"], configPartial["views"]);

    const doc = GetDoc();
    const vm = doc.GetViewMgr();
    vm.AddView(viewId);
    let wsMgr = GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("view_library.view.promoted");
  };

  const handleDemote = async viewId => {
    const configPartial = {
      views: (chartConfig["views"] || []).filter(id => id !== viewId)
    };
    await handleChartConfigUpdate(configPartial);
    setChartConfig(p => ({ ...p, ...configPartial }));
    updateViewsList(chartConfig["ID"], configPartial["views"]);

    let wsMgr = GetWSMgr();
    const doc = GetDoc();
    const vm = doc.GetViewMgr();
    const view = vm.GetViewByID(viewId);
    const eventTracker = wsMgr.GetUsageTracker();
    if (view) {
      vm.RemoveView(view);
      eventTracker.ReportEvent("view_library.view.demoted");
    }
  };

  const manager = {
    renameState: {
      renamingId,
      setRenamingId
    },
    creatingState: {
      isCreatingNew,
      setCreatingNew
    },
    currentTabState: {
      setCurrentTab
    },
    selectedTemplateState: {
      selectedId,
      setSelectedId
    },
    editingState: {
      editingIds,
      setEditingIds
    },
    promoteMgr: {
      getIsPromoted,
      handlePromote,
      handleDemote
    },
    removeView: (id, set) => {
      RemoveView(id, set);
      setRemovedViews(p => [...p, id]);
    }
  };

  const actionNode = (
    <Flex direction="row" padding="12px 16px" justifyContent="flex-end" gap="8px">
      {currentTab === 0 ? (
        <Button
          dataTestId="view-library-switch-to-shared-view"
          onClick={() => setCurrentTab(1)}
          modifier={"secondary"}
          className={cx("oc-special-secondary", "oc-special-secondary--medium")}
        >
          {appstr("switch to shared views to create new views")}
        </Button>
      ) : (
        <>
          <Button
            dataTestId="view-library-action-edit-view"
            onClick={handleEdit}
            disabled={!canEditView}
            tooltipProps={
              showEditViewTooltip
                ? {
                    message: appstr("Finish editing your chart to update these rules")
                  }
                : undefined
            }
            modifier={"secondary"}
            className={cx("oc-special-secondary", "oc-special-secondary--medium")}
          >
            {appstr("Edit")} {appstr("Template")}
          </Button>
          <Button
            dataTestId="view-library-action-add-view"
            onClick={handleAddTemplate}
            modifier={"secondary"}
            className={cx("oc-special-secondary", "oc-special-secondary--medium")}
          >
            {appstr("addtemplate")}
          </Button>
        </>
      )}
    </Flex>
  );

  return (
    <div onKeyDown={e => e.stopPropagation()}>
      <CentralizedModalContainer show={props.open}>
        <Modal
          id="view-library-modal"
          show={props.open}
          centerScreen={true}
          onClose={handleCloseAttempt}
          fullScreen={true}
        >
          <DialogTitle divider={true} onClose={handleCloseAttempt}>
            <span className={"ocn-explorer-icon-label"}>
              <Icon size={"medium"} name={"books"} />
              {GetDoc()?.IsPositionDocument()
                ? appstr("Position view library")
                : appstr("View library")}
            </span>
          </DialogTitle>
          <DialogContent customContentClass={"ocn-chart-explorer-content"}>
            <div className="ocn-chart-explorer">
              <div className={cx("ocn-chart-explorer-column", "ocn-chart-explorer-tabular")}>
                <Tabs
                  tabIndex={currentTab}
                  customClass={"ocn-chart-explorer-tabs"}
                  customContentClass={cx(
                    "ocn-chart-explorer-tabs-content",
                    "oc-view-library-tabs-content"
                  )}
                  tabs={[
                    {
                      header: appstr("System"),
                      dataTestId: "view-library-system-tab",
                      content: (
                        <Fragment key="system">
                          <ViewLibraryTab
                            manager={manager}
                            actionNode={actionNode}
                            set={enumChartTemplateSet.systemtemplates}
                          />
                        </Fragment>
                      )
                    },
                    {
                      header: appstr("Shared"),
                      dataTestId: "view-library-shared-tab",
                      content: (
                        <Fragment key="shared">
                          <ViewLibraryTab
                            manager={manager}
                            actionNode={actionNode}
                            set={enumChartTemplateSet.sharedtemplates}
                          />
                        </Fragment>
                      )
                    }
                  ]}
                  onTabChange={handleTabChange}
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions divider={true}>
            <DialogActions.Primary
              dataTestId="view-library-action-save"
              disabled={!vm.ChartTemplatesDirty()}
              onClick={handleSave}
            >
              {appstr("Save")}
            </DialogActions.Primary>
          </DialogActions>
        </Modal>
      </CentralizedModalContainer>
    </div>
  );
};

export default ViewLibraryModal;
